/**
 * Centralized UI color system for Shmerge's *chrome* — backdrop, glass jar,
 * HUD chips, buttons, text, glows. NOT the candy tier colors (those live in
 * game/shapes.ts and never change); these tokens are chosen to harmonize with
 * that fixed, saturated rainbow.
 *
 * Every palette ships a full, cohesive set of tokens. Pick one live in Settings
 * or via `?palette=<name>`; the choice persists in localStorage. Mirrors the
 * shape-skin pattern in game/shapes.ts (`shapeStyle()` / `?shape=`).
 *
 * Why a few named, hand-tuned palettes instead of one generic theme: the candy
 * spans the whole hue wheel, so the chrome has to be a deliberate, low-chroma
 * stage that lets the shapes pop. Each direction below commits to one mood.
 */

/** All chrome color tokens, as 0xRRGGBB numbers (Phaser fills take numbers). */
export interface Palette {
  /** Stable id — used in localStorage / `?palette=` and as the React-free key. */
  name: string;
  /** Human label for the Settings picker. */
  label: string;
  /** One-line mood, shown under the label in the picker / compare page. */
  blurb: string;

  // ── Backdrop ──────────────────────────────────────────────────────────────
  bgTop: number;       // vertical gradient, lifted top tone
  bgBottom: number;    // vertical gradient, deep bottom tone
  glow: number;        // ambient radial glow behind the action
  bokeh: number[];     // 5 slow-drifting decorative orbs

  // ── Accents ───────────────────────────────────────────────────────────────
  primary: number;     // the signature accent: links, "ON" states, highlights
  secondary: number;   // supporting accent for contrast moments
  button: number;      // primary CTA base (gradient is derived by lightening)
  /** Icon-chip accent (HUD cog/tilt, menu cog) when it should differ from
   *  `primary` — e.g. Picnic's wood-toned chrome. Falls back to `primary`. */
  iconAccent?: number;

  // ── Text ──────────────────────────────────────────────────────────────────
  text: number;        // primary copy + big numbers
  textSoft: number;    // secondary body copy
  textMuted: number;   // captions / inactive
  textLabel: number;   // small all-caps labels
  textFaint: number;   // faintest (version string, privacy link)

  // ── HUD chips (score / coins lozenges) ─────────────────────────────────────
  chipFill: number;    // glassy chip body
  chipStroke: number;  // chip accent outline
  chipShadow: number;  // soft drop shadow under chips + jar

  // ── Surfaces (cards, toggle tracks, secondary buttons, dock slots) ─────────
  surface: number;       // standard panel / track / secondary-button fill
  surfaceBorder: number; // panel / track hairline border
  surfaceDeep: number;   // deeper modal fill (how-to-play / name input)
  toggleOn: number;      // active toggle track (a muted shade of `primary`)

  // ── Glass jar ──────────────────────────────────────────────────────────────
  jarFill: number;     // translucent wall fill
  jarEdge: number;     // wall inner-edge highlight stroke
  jarRim: number;      // glossy inner rim streak

  // ── Semantic ───────────────────────────────────────────────────────────────
  coin: number;        // coin gold — kept warm across palettes (coins read gold)
  danger: number;      // game-over title / warnings

  // ── Mode & container treatment ─────────────────────────────────────────────
  /** Light palettes flip the few remaining dark-stage assumptions (the black
   *  status-bar fade, dark steering-wheel well). Omitted = 'dark'. */
  mode?: 'light' | 'dark';
  /** When set, the container renders as wood planks (see util/wood.ts) instead
   *  of translucent glass; jarRim still paints the sun-lit inner edge. */
  wood?: {
    base: number;       // plank body
    grainDark: number;  // grain streaks / plank joints
    grainLight: number; // top-light on the plank face
    edge: number;       // outline + end-grain edge
  };
}

/**
 * ORCHARD DUSK — warm plum-berry candy night. The default: closest in spirit to
 * the shipping warm backdrop, evolved into a cohesive system. Mint + rose
 * accents over deep grape; reads as a softly-lit confectionery.
 */
const ORCHARD_DUSK: Palette = {
  name: 'orchard',
  label: 'Plum',
  blurb: 'Warm plum night · mint & rose',
  bgTop: 0x2d1a44,
  bgBottom: 0x130920,
  glow: 0xc65cc0,
  bokeh: [0xffb3d9, 0xb3f0e0, 0xc9b3ff, 0xfff0b3, 0xffc7b0],
  primary: 0x6fe3a6,
  secondary: 0xff7eb6,
  button: 0x7d53e0,
  text: 0xf0ebf7,
  textSoft: 0xc3b6d6,
  textMuted: 0x9a8fb5,
  textLabel: 0x7d7298,
  textFaint: 0x5c5278,
  chipFill: 0x271a3f,
  chipStroke: 0x9a7bff,
  chipShadow: 0x0a0414,
  surface: 0x1f1636,
  surfaceBorder: 0x3a2b54,
  surfaceDeep: 0x150d26,
  toggleOn: 0x305a45,
  jarFill: 0x6e4ea0,
  jarEdge: 0xffc8e8,
  jarRim: 0xffdaf2,
  coin: 0xffd23f,
  danger: 0xff6b9d,
};

/**
 * PICNIC — the light mode. A soft claymorphic kawaii daytime picnic: warm
 * beige-paper sky, strawberry + honey accents, cocoa text, clay-brown shadows
 * instead of black, and a wooden crate in place of the glass jar. The chrome
 * stays in one warm family (rose / honey / terracotta wood) so nothing fights
 * the candy, and the neutral low-chroma stage gives the fixed rainbow
 * (especially the purple hexagon and magenta plus, which sink into the plum
 * night) its strongest contrast.
 */
const PICNIC: Palette = {
  name: 'picnic',
  label: 'Picnic',
  blurb: 'Warm beige day · strawberry & honey',
  mode: 'light',
  bgTop: 0xf9f1de,
  bgBottom: 0xecdcbb,
  glow: 0xffe9c4,
  bokeh: [0xff9eb8, 0x8fd9a8, 0x9ec3ff, 0xffd166, 0xffab8a],
  primary: 0xe0507a,
  secondary: 0xd98e2b,
  button: 0xf0608f,
  iconAccent: 0xc1714e,
  text: 0x4e3a28,
  textSoft: 0x6f5740,
  textMuted: 0x8c7355,
  textLabel: 0xa68b66,
  textFaint: 0xbfa988,
  chipFill: 0xfff8e9,
  chipStroke: 0xd8a468,
  chipShadow: 0xb08d5f,
  surface: 0xfdf5e4,
  surfaceBorder: 0xe5cda6,
  surfaceDeep: 0xf3e7cd,
  toggleOn: 0xf3c1d2,
  jarFill: 0xd9a86c,
  jarEdge: 0x8f6238,
  jarRim: 0xf4dfb2,
  coin: 0xd98f1f,
  danger: 0xd9485f,
  wood: { base: 0xdba76b, grainDark: 0xb5824c, grainLight: 0xf0cd96, edge: 0x8f6238 },
};

/** Display order. First entry is the default. */
export const PALETTES: Palette[] = [ORCHARD_DUSK, PICNIC];

const DEFAULT_PALETTE = ORCHARD_DUSK;
const STORE_KEY = 'shmerge-ui-palette';

function findPalette(name: string | null | undefined): Palette | undefined {
  if (!name) return undefined;
  return PALETTES.find((p) => p.name === name);
}

/**
 * The active chrome palette. Resolution order: `?palette=<name>` (live compare),
 * then `localStorage['shmerge-ui-palette']`, then the default (Orchard Dusk).
 * Read fresh each call so a scene rebuild picks up a just-changed selection,
 * exactly like `shapeStyle()`.
 */
export function activePalette(): Palette {
  if (typeof window === 'undefined') return DEFAULT_PALETTE;
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('palette');
    const q = findPalette(fromQuery);
    if (q) return q;
    if (typeof localStorage !== 'undefined') {
      const stored = findPalette(localStorage.getItem(STORE_KEY));
      if (stored) return stored;
    }
  } catch {
    /* fall through to default */
  }
  return DEFAULT_PALETTE;
}

/** Persist the chosen palette. Scenes re-read via activePalette() on rebuild. */
export function setActivePalette(name: string): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(STORE_KEY, name);
    // A ?palette= override outranks localStorage in activePalette(), so an
    // explicit pick in Settings would silently not apply while one is present
    // — drop the param from the URL.
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      const url = new URL(window.location.href);
      if (url.searchParams.has('palette')) {
        url.searchParams.delete('palette');
        window.history.replaceState(null, '', url);
      }
    }
  } catch {
    /* ignore */
  }
}

/** 0xRRGGBB → '#rrggbb' for Phaser Text `color` / DOM styles. */
export function css(color: number): string {
  return `#${(color & 0xffffff).toString(16).padStart(6, '0')}`;
}

/** Lighten a 0xRRGGBB color toward white by `amt` (0..1). */
export function lighten(color: number, amt: number): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const lr = Math.round(r + (255 - r) * amt);
  const lg = Math.round(g + (255 - g) * amt);
  const lb = Math.round(b + (255 - b) * amt);
  return (lr << 16) | (lg << 8) | lb;
}
